import React, { useEffect, useId, useState } from 'react'
import { PROJECT } from 'app-constants'
import { INotification, ITrigger, IProject, ITable, IProcess, IProcessObject, IActionHeader } from 'types'
import api from 'helpers/api'
import { isValidHttpUrl } from 'helpers/utils'
import { useApplicationStore } from 'hooks/application'
import Button from 'components/button'
import Dialog from 'components/dialog'
import Field from 'components/field'
import FilterMenu, { FIELD_MENU_BUTTON } from 'components/filter-menu'
import Input from 'components/input'
import Tooltip from 'components/tooltip'
import { BLUE_BUTTON } from 'components/save-buttons'
import { Cross, Plus } from 'components/icons'
import { eventLabel, groupEvents } from 'components/audit/eventFilter'
import {
  eventCount,
  isTableTriggerSelected,
  isProcessTriggerSelected,
  toEventName,
  triggersForEvents
} from 'views/settings/components/notifications/helpers/utils'

export interface IFormValues {
  webhookUrl: string
  description: string
  customHeaders: IActionHeader[]
  triggers: ITrigger[]
  processes: Pick<IProcess, 'publicId' | 'name'>[]
  tables: Pick<ITable, 'publicId' | 'name'>[]
}

const LABEL = 'text-[13px] font-medium text-body'
const FIELD = 'flex flex-col gap-1.5'
const HINT = 'text-xs leading-4 text-muted'

/** The documents or tables a webhook is limited to: a field's button naming them, "All" when there are none. */
const ResourcePicker: React.FC<{
  noun: { one: string; many: string }
  options: Array<{ publicId: string; name: string }> | undefined
  selected: string[]
  onChange: (ids: string[]) => void
  /** Its accessible name, as the field's label says it. */
  label: string
}> = ({ noun, options, selected, onChange, label }) => {
  const names = (options ?? [])
    .filter((option) => selected.includes(option.publicId))
    .map((option) => option.name)
    .join(', ')
  return (
    <FilterMenu
      noun={noun}
      groups={[{ options: (options ?? []).map((option) => ({ value: option.publicId, label: option.name })) }]}
      selected={selected}
      onChange={(ids) => onChange(ids ?? [])}
      pick
      loading={options === undefined}
      label={label}
      buttonLabel={names || `All ${noun.many}`}
      labelInFull={names}
      buttonClassName={FIELD_MENU_BUTTON}
      matchWidth
    />
  )
}

interface WebhookDialogProps {
  project: IProject
  /** The webhook to edit. Without one, the dialog adds one. */
  notification?: INotification
  onClose: () => void
  /** Saves it; the caller closes the dialog when it's saved, and says why when it isn't. */
  onSubmit: (values: IFormValues) => void | Promise<void>
}

/**
 * Adds or edits a webhook, in our dialog: its description and URL, the events that send to it (grouped as
 * the audit log groups them), the documents and tables it's limited to when its events concern them, and
 * any headers sent with it. Save waits for a description, a valid URL and an event.
 */
const WebhookDialog: React.FC<WebhookDialogProps> = ({ project, notification, onClose, onSubmit }) => {
  const { displayErrorMessage } = useApplicationStore()
  const id = useId()
  const [submitting, setSubmitting] = useState(false)
  const [eventTypes, setEventTypes] = useState<string[]>()
  const [documents, setDocuments] = useState<IProcessObject[]>()
  const [tables, setTables] = useState<ITable[]>()
  const [values, setValues] = useState<IFormValues>({
    webhookUrl: notification ? notification.actions[0].webhookUrl : '',
    description: notification ? notification.description : '',
    customHeaders: notification ? notification.actions[0].customHeaders : [],
    triggers: notification ? notification.triggers : [],
    tables: notification ? notification.tables : [],
    processes: notification ? notification.processes : []
  })

  useEffect(() => {
    // Responses for a hub no longer shown are dropped, success or failure.
    let cancelled = false
    const failed = (error: unknown) => {
      if (!cancelled) displayErrorMessage(error)
    }
    api
      .getNotificationEventTypes()
      .then((response) => {
        if (!cancelled) setEventTypes(response.data)
      })
      .catch(failed)
    api
      .getProjectResources(project.publicId, {})
      .then((response) => {
        if (cancelled) return
        setDocuments(response.data.flatMap((item) => (item.process ? [item.process] : [])))
        setTables(response.data.flatMap((item) => (item.table ? [item.table] : [])))
      })
      .catch(failed)
    return () => {
      cancelled = true
    }
  }, [project.publicId])

  // Each event once: a webhook subscribed under an event's legacy name and its product name has it twice.
  const selectedEvents = [...new Set(values.triggers.map(toEventName))]
  const urlInvalid = values.webhookUrl !== '' && !isValidHttpUrl(values.webhookUrl)
  const ready = values.description.trim() !== '' && isValidHttpUrl(values.webhookUrl) && values.triggers.length > 0

  const setHeader = (index: number, header: IActionHeader) =>
    setValues({ ...values, customHeaders: values.customHeaders.map((each, i) => (i === index ? header : each)) })

  const submit = async () => {
    setSubmitting(true)
    await onSubmit(values)
    setSubmitting(false)
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={notification ? 'Edit Webhook' : 'Add Webhook'}
      description={`Send events from this ${PROJECT} to a URL.`}
      footer={
        <>
          <Button internalType="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            internalType="outline"
            className={BLUE_BUTTON}
            disabled={!ready}
            isLoading={submitting}
            onClick={submit}
          >
            {notification ? 'Update Webhook' : 'Add Webhook'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Field label="Description" htmlFor={`${id}-description`} className={FIELD} labelClassName={LABEL}>
          <Input
            id={`${id}-description`}
            placeholder="Send new rows to Power Automate"
            value={values.description}
            onChange={(event) => setValues({ ...values, description: event.target.value })}
          />
        </Field>

        <Field label="URL" htmlFor={`${id}-url`} className={FIELD} labelClassName={LABEL}>
          <Input
            id={`${id}-url`}
            type="url"
            placeholder="https://"
            autoComplete="off"
            value={values.webhookUrl}
            aria-invalid={urlInvalid || undefined}
            aria-describedby={urlInvalid ? `${id}-url-error` : undefined}
            onChange={(event) => setValues({ ...values, webhookUrl: event.target.value })}
          />
          {urlInvalid && (
            <p id={`${id}-url-error`} className={`${HINT} text-red`}>
              Enter a full URL, such as https://example.com/webhook
            </p>
          )}
        </Field>

        <div className={FIELD}>
          <span className={LABEL}>Events</span>
          <FilterMenu
            noun={{ one: 'event', many: 'events' }}
            groups={groupEvents(eventTypes ?? []).map((group) => ({
              label: group.label,
              options: group.events.map((event) => ({ value: event, label: eventLabel(event) }))
            }))}
            selected={selectedEvents}
            onChange={(events) => setValues({ ...values, triggers: triggersForEvents(values.triggers, events ?? []) })}
            pick
            loading={eventTypes === undefined}
            label="Events"
            buttonLabel={
              selectedEvents.length === 0 ? (
                <span className="text-faint">Choose events</span>
              ) : (
                eventCount(selectedEvents.length)
              )
            }
            buttonClassName={FIELD_MENU_BUTTON}
            matchWidth
          />
        </div>

        {isProcessTriggerSelected(values.triggers) && (
          <div className={FIELD}>
            <span className={LABEL}>Documents</span>
            <ResourcePicker
              noun={{ one: 'document', many: 'documents' }}
              options={documents}
              selected={values.processes.map((each) => each.publicId)}
              onChange={(ids) =>
                setValues({ ...values, processes: (documents ?? []).filter((each) => ids.includes(each.publicId)) })
              }
              label="Documents"
            />
            <p className={HINT}>Events are sent for all documents unless specific documents are chosen.</p>
          </div>
        )}

        {isTableTriggerSelected(values.triggers) && (
          <div className={FIELD}>
            <span className={LABEL}>Tables</span>
            <ResourcePicker
              noun={{ one: 'table', many: 'tables' }}
              options={tables}
              selected={values.tables.map((each) => each.publicId)}
              onChange={(ids) =>
                setValues({ ...values, tables: (tables ?? []).filter((each) => ids.includes(each.publicId)) })
              }
              label="Tables"
            />
            <p className={HINT}>Events are sent for all tables unless specific tables are chosen.</p>
          </div>
        )}

        <div className={FIELD}>
          <div className="flex flex-col gap-0.5">
            <span className={LABEL}>Custom headers</span>
            <span className={HINT}>Sent with every request, for example to authenticate it.</span>
          </div>
          {values.customHeaders.map((header, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                aria-label={`Header ${index + 1} key`}
                placeholder="Key"
                value={header.key}
                onChange={(event) => setHeader(index, { ...header, key: event.target.value })}
              />
              <Input
                aria-label={`Header ${index + 1} value`}
                placeholder="Value"
                value={header.value}
                onChange={(event) => setHeader(index, { ...header, value: event.target.value })}
              />
              <Tooltip content="Remove header">
                <Button
                  internalType="transparent"
                  aria-label={`Remove header ${index + 1}`}
                  className="size-9 shrink-0 p-0"
                  onClick={() =>
                    setValues({ ...values, customHeaders: values.customHeaders.filter((_, i) => i !== index) })
                  }
                >
                  <Cross />
                </Button>
              </Tooltip>
            </div>
          ))}
          <div>
            <Button
              internalType="outline"
              onClick={() => setValues({ ...values, customHeaders: [...values.customHeaders, { key: '', value: '' }] })}
            >
              <Plus />
              Add Header Key-Value
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default WebhookDialog
